import { addMinutes, isBefore } from 'date-fns';
import { t } from 'i18next';

import { FindProjectModel } from '../../../typings/api/skymap/rest/v0/.common';
import { CoordinateSystemModel, GeoidModel } from '../../../typings/api/skymap/rest/v1/.common';
import { errorUtils } from '../../react/api/error-handling';
import { FeatureFlagsManager } from '../core/feature-flags-manager';
import { SkyMapAxiosServiceFactory } from '../services/axios/skymap-axios-service-factory';
import { isDefined } from '../utils/variables';
import { PermissionStore } from './permission-store';

const cacheExpirationInMinutes = 30;

export class ProjectStore {
  static get instance() {
    return ProjectStore._instance;
  }

  private projectInfoLastFetched: Date | undefined;

  get project() {
    return this._project;
  }

  get geoid() {
    return this._geoid;
  }

  get coordinateSystem() {
    return this._coordinateSystem;
  }

  reset() {
    ProjectStore._instance = new ProjectStore();
  }

  getCoordinateSystemOrDefault() {
    return (
      this.coordinateSystem ?? {
        name: t('defaultCoordinateSystemName', { ns: 'skyviewScale' }),
        proj4: 'local',
        id: '',
        authority: '',
      }
    );
  }

  // Adds a temporary property that describes if the project has design tool access.
  get isProjectOverridingDesignToolAccess(): boolean {
    return this._project?.isCompanyAccountOverridingDesignToolAccess ?? false;
  }

  private _project?: FindProjectModel;
  private _coordinateSystem?: CoordinateSystemModel;
  private _geoid?: GeoidModel;

  private static _instance: ProjectStore = new ProjectStore();

  private constructor() {
    /*  */
  }

  hasCompanyAdmin(userId: string) {
    return isDefined(this.project!.companyAdmins.find((x) => x.id === userId));
  }

  clearCache() {
    this.projectInfoLastFetched = undefined;
  }

  isCacheActive() {
    return (
      isDefined(this.projectInfoLastFetched) &&
      isBefore(new Date(), addMinutes(this.projectInfoLastFetched, cacheExpirationInMinutes))
    );
  }

  updateCacheTime() {
    this.projectInfoLastFetched = new Date();
  }

  async reload() {
    if (!isDefined(this._project)) {
      return;
    }

    this.clearCache();
    await this.fetchProjectInfo(this._project.id);
  }

  async fetchProjectInfo(projectId: string): Promise<void> {
    if (this.isCacheActive()) {
      return;
    }

    // Note:
    // Do not reset _project etc during fetch of project data. If project is set, project to fetch
    // data for is for the same project. The current project data is used to display correct menu
    // items (via licenses), and if we set _project to undefined during fetch, menu items will
    // disappear as licenses exist in _project.userCompanyLicenses.
    const [projectResult, coordinateSystem, geoid] = await Promise.all([
      SkyMapAxiosServiceFactory.instance.createProjectServiceV0().getById({
        query: { projectId },
      }),
      this.getCoordinateSystem(projectId),
      this.getProjectGeoid(projectId),
    ]);

    this._project = projectResult.data;
    this._coordinateSystem = coordinateSystem;
    this._geoid = geoid;

    this.updateCacheTime();

    // Temporary solution to have the permissions loaded when a project have been loaded.
    // With React transition Cerbos hooks will listen to project state changes.
    if (
      FeatureFlagsManager.instance.isEnabled('SK-4477-access-management') &&
      PermissionStore.instance.requirePermissionsReload(projectId)
    ) {
      await PermissionStore.instance.refreshAccessState();
    }
  }

  private async getCoordinateSystem(projectId: string): Promise<CoordinateSystemModel | undefined> {
    try {
      const result = await SkyMapAxiosServiceFactory.instance
        .createProjectServiceV1()
        .getCoordinateSystem({
          path: { projectId },
        });

      return result.data;
    } catch (err: unknown) {
      if (
        !errorUtils.isResponseError(
          err,
          'resource_not_found',
          'project:coordinate_system_not_found',
        )
      ) {
        throw err;
      }
    }
  }

  private async getProjectGeoid(projectId: string): Promise<GeoidModel | undefined> {
    try {
      const result = await SkyMapAxiosServiceFactory.instance.createProjectServiceV1().getGeoid({
        path: { projectId },
      });

      return result.data;
    } catch (err: unknown) {
      if (!errorUtils.isResponseError(err, 'resource_not_found', 'project:geoid_not_found')) {
        throw err;
      }
    }
  }

  /**
   * Sets the coordinate system in db and updates the value in project store.
   */
  async setCoordinateSystem(coordinateSystemId: string) {
    const result = await SkyMapAxiosServiceFactory.instance
      .createProjectServiceV1()
      .putCoordinateSystem({
        body: {
          id: coordinateSystemId,
        },
        path: {
          projectId: this.project!.id,
        },
      });

    this._coordinateSystem = result.data;
  }

  /**
   * Sets the coordinate system in db and updates the value in project store.
   */
  async setGeoid(geoidId: string) {
    const result = await SkyMapAxiosServiceFactory.instance.createProjectServiceV1().putGeoid({
      body: {
        id: geoidId,
      },
      path: {
        projectId: this.project!.id,
      },
    });

    this._geoid = result.data;
  }

  setName(value: string) {
    if (this._project) {
      this._project.name = value;
    }
  }
  setDescription(value?: string) {
    if (this._project) {
      this._project.description = value ?? null;
    }
  }
}
